/**
 * Cohesive ChatKit — Citations, ErrorMsg, WorkingSignal.
 *
 * Working signals replace spinners (which the design system bans): typewriter
 * (streamed present-tense status), edge pulse, skeleton lines, liquid surface.
 *
 * Ported from cohesive-design-system/showcase/lib/chat-kit.jsx.
 */
import { AlertTriangle, RotateCcw, Sparkles } from "lucide-react";
import * as React from "react";

import { cn } from "@/lib/utils";
import { Alert, AlertDescription, AlertTitle } from "../ui/alert";
import { Button } from "../ui/button";
import { Skeleton } from "../ui/skeleton";
import { Favicon } from "./atoms";

export interface CitationSource {
  domain: string;
  url?: string;
  title?: string;
}

/** Numbered source pills under the final answer. */
export function Citations({
  sources,
  className,
}: {
  sources: CitationSource[];
  className?: string;
}) {
  if (sources.length === 0) return null;
  return (
    <div className={cn("mt-3 flex flex-wrap gap-1.5", className)}>
      {sources.map((s, i) => {
        const pillClass =
          "inline-flex items-center gap-1.5 rounded-full bg-card px-2.5 py-1.5 font-sans text-xs text-n-70 shadow-hairline outline-none transition-shadow duration-150 ease-out hover:shadow-[0_0_0_1px_var(--n-30)] focus-visible:outline-2 focus-visible:outline-rust";
        const inner = (
          <>
            <Favicon domain={s.domain} size={16} />
            <span className="text-n-50">{i + 1}</span>
            {s.domain}
          </>
        );
        return s.url ? (
          <a
            key={i}
            href={s.url}
            target="_blank"
            rel="noreferrer"
            title={s.title}
            className={pillClass}
          >
            {inner}
          </a>
        ) : (
          <button key={i} type="button" title={s.title} className={pillClass}>
            {inner}
          </button>
        );
      })}
    </div>
  );
}

/** Failure banner with optional retry. */
export function ErrorMsg({
  title = "Something went wrong",
  text,
  onRetry,
  retryLabel = "Try again",
  className,
}: {
  title?: React.ReactNode;
  text?: React.ReactNode;
  onRetry?: () => void;
  retryLabel?: string;
  className?: string;
}) {
  return (
    <Alert variant="error" className={className}>
      <span className="text-spectrum-red">
        <AlertTriangle className="size-[18px]" />
      </span>
      <div className="flex min-w-0 flex-col gap-1">
        <AlertTitle className="mb-0 text-sm">{title}</AlertTitle>
        {text && (
          <AlertDescription className="text-xs text-n-70">
            {text}
          </AlertDescription>
        )}
        {onRetry && (
          <div className="mt-1 flex">
            <Button variant="secondary" size="sm" onClick={onRetry}>
              <RotateCcw /> {retryLabel}
            </Button>
          </div>
        )}
      </div>
    </Alert>
  );
}

export type WorkingSignalVariant =
  | "typewriter"
  | "edge"
  | "skeleton"
  | "liquid";

/** In-flight agent activity. Spinners are banned; pick a signal. */
export function WorkingSignal({
  variant = "typewriter",
  label = "Working",
  text,
  className,
}: {
  variant?: WorkingSignalVariant;
  label?: string;
  text?: string;
  className?: string;
}) {
  if (variant === "skeleton") {
    return (
      <div className={cn("flex flex-col gap-2.5", className)}>
        <span className="inline-flex items-center gap-2 font-sans text-xs font-medium text-n-50">
          <Sparkles className="size-3.5 text-rust" />{" "}
          <span className="animate-pulse">{label}</span>
        </span>
        <div className="flex flex-col gap-2">
          <Skeleton className="h-3 w-[92%] rounded-full" />
          <Skeleton className="h-3 w-[78%] rounded-full" />
          <Skeleton className="h-3 w-[85%] rounded-full" />
        </div>
      </div>
    );
  }
  if (variant === "edge") {
    return (
      <div className={cn("relative pl-3", className)}>
        <span className="absolute bottom-0.5 left-0 top-0.5 w-0.5 animate-pulse rounded-full bg-rust" />
        <div className="font-sans text-xs font-medium text-n-50">
          <span className="animate-pulse">{label}</span>
        </div>
        {text && <div className="mt-1 font-sans text-sm text-n-60">{text}</div>}
      </div>
    );
  }
  if (variant === "liquid") {
    return (
      <div
        className={cn(
          "relative flex h-14 items-center gap-3 overflow-hidden rounded-md bg-card px-4 shadow-hairline",
          className,
        )}
      >
        <span className="absolute inset-[-40%] animate-drift-1 bg-[radial-gradient(circle_at_30%_50%,var(--plum),transparent_60%)] opacity-55 blur-[36px] motion-reduce:animate-none" />
        <span className="absolute inset-[-40%] animate-drift-2 bg-[radial-gradient(circle_at_70%_40%,var(--gold),transparent_55%)] opacity-55 blur-[36px] motion-reduce:animate-none" />
        <span className="absolute inset-[-40%] animate-drift-3 bg-[radial-gradient(circle_at_50%_70%,var(--rust),transparent_55%)] opacity-55 blur-[36px] motion-reduce:animate-none" />
        <Sparkles className="relative z-[1] size-4 text-n-90" />
        <span className="relative z-[1] font-sans text-xs font-medium text-n-90">
          {label}
        </span>
      </div>
    );
  }
  // typewriter — streamed present-tense status (mono is correct here)
  return (
    <div
      className={cn("font-mono text-xs leading-normal text-n-70", className)}
    >
      {text ?? label}
      <span className="ml-0.5 inline-block h-[1em] w-px animate-caret bg-rust align-text-bottom motion-reduce:animate-none" />
    </div>
  );
}
